import { useState } from 'react';
import { alpha } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, Button, Skeleton,
  Stack, Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Alert, IconButton, Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { HelpOutline as HelpIcon } from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { formatCurrency } from '@/utils/format';
import { employeeAllowancesApi } from '@/api/endpoints/hr';
import apiClient from '@/api/client';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import PayslipExplainPanel from '@/components/payroll/PayslipExplainPanel';
import { InfoRow } from './shared';

interface PayslipDetail {
  id: string;
  period_year: number;
  period_month: number;
  gross_pay: number;
  paye: number;
  pension: number;
  nhf: number;
  nsitf: number;
  itf: number;
  total_deductions: number;
  net_pay: number;
  run_id: string;
}

interface CompensationPayrollTabProps {
  emp: any;
  currencyCode: string;
}

export default function CompensationPayrollTab({ emp, currencyCode }: CompensationPayrollTabProps) {
  const [subTab, setSubTab] = useState(0);

  return (
    <Box>
      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{
          mb: 2.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.sm,
            minHeight: 36, py: 0.5, px: 2, borderRadius: '8px', mr: 1,
          },
          '& .Mui-selected': { bgcolor: 'primary.main', color: 'white !important' },
          '& .MuiTabs-indicator': { display: 'none' },
        }}
      >
        <Tab label="Compensation" />
        <Tab label="Payslips" />
      </Tabs>

      {subTab === 0 && <CompensationView emp={emp} currencyCode={currencyCode} />}
      {subTab === 1 && <PayslipsView currencyCode={currencyCode} />}
    </Box>
  );
}

function CompensationView({ emp, currencyCode }: { emp: any; currencyCode: string }) {
  const { data: allowances = [] } = useQuery({
    queryKey: ['employee-allowances', emp.id],
    queryFn: () => employeeAllowancesApi.list(emp.id).then(r => r.data),
    enabled: !!emp.id,
  });

  const activeAllowances = allowances.filter((a: any) => a.is_active);
  const dynamicAllowancesTotal = activeAllowances.reduce((s: number, a: any) => s + Number(a.amount), 0);
  const grossPay = Number(emp.basic_salary || 0) + Number(emp.housing_allowance || 0) + Number(emp.transport_allowance || 0) + Number(emp.meal_allowance || 0) + dynamicAllowancesTotal;

  return (
    <Stack spacing={3}>
      {/* Current Compensation */}
      <Card>
        <CardContent>
          <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 2 }}>Current Compensation</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', textTransform: 'uppercase', fontWeight: TYPE.semibold, mb: 0.5 }}>
                  Basic Salary
                </Typography>
                <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>
                  {formatCurrency(emp.basic_salary, currencyCode)}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', textTransform: 'uppercase', fontWeight: TYPE.semibold, mb: 0.5 }}>
                  Allowances
                </Typography>
                <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>
                  {formatCurrency(Number(emp.housing_allowance || 0) + Number(emp.transport_allowance || 0) + Number(emp.meal_allowance || 0) + dynamicAllowancesTotal, currencyCode)}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', textTransform: 'uppercase', fontWeight: TYPE.semibold, mb: 0.5 }}>
                  Gross Pay
                </Typography>
                <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, color: 'primary.main' }}>
                  {formatCurrency(grossPay, currencyCode)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Allowances Detail */}
      <Card>
        <CardContent>
          <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 2 }}>Allowances Detail</Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontSize: TYPE.xs }}>Allowance Type</TableCell>
                <TableCell align="right" sx={{ fontSize: TYPE.xs }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontSize: TYPE.sm }}>Housing</TableCell>
                <TableCell align="right" sx={{ fontSize: TYPE.sm }}>{formatCurrency(emp.housing_allowance, currencyCode)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontSize: TYPE.sm }}>Transport</TableCell>
                <TableCell align="right" sx={{ fontSize: TYPE.sm }}>{formatCurrency(emp.transport_allowance, currencyCode)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontSize: TYPE.sm }}>Meal</TableCell>
                <TableCell align="right" sx={{ fontSize: TYPE.sm }}>{formatCurrency(emp.meal_allowance, currencyCode)}</TableCell>
              </TableRow>
              {activeAllowances.map((a: any) => (
                <TableRow key={a.id}>
                  <TableCell sx={{ fontSize: TYPE.sm }}>{a.allowance_type_name}</TableCell>
                  <TableCell align="right" sx={{ fontSize: TYPE.sm }}>{formatCurrency(a.amount, currencyCode)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Stack>
  );
}

/** Modal explaining PAYE calculation per Finance Act 2020 — same as admin PayInfoTab */
function PAYEExplanationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>How Are My Deductions Calculated?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              PAYE — Finance Act 2020
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1, fontSize: TYPE.xs }}>
              1. Gross Annual = (Basic + Allowances) x 12{'\n'}
              2. CRA = Max(N200K, 1% of Gross) + 20% of Gross{'\n'}
              3. Taxable = Gross - Pension - NHF - CRA{'\n'}
              4. Progressive Brackets: 7% / 11% / 15% / 19% / 21% / 24%{'\n'}
              5. Minimum Tax = 1% of Gross{'\n'}
              6. Monthly PAYE = Max(Computed, Min Tax) / 12
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Other Statutory Deductions
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1, fontSize: TYPE.xs }}>
              Pension = 8% of (Basic + Housing + Transport){'\n'}
              NHF = 2.5% of Basic Salary{'\n'}
              NSITF = 1% of Gross (employer-side){'\n'}
              ITF = 1% of Gross (employer-side, if 5+ employees)
            </Typography>
          </Box>
          <Alert severity="info">
            All calculations use Decimal precision (to the kobo), not rounded estimates. Your deductions are computed by the payroll engine, not estimated on this page.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function PayslipsView({ currencyCode }: { currencyCode: string }) {
  const [selectedPayslip, setSelectedPayslip] = useState<PayslipDetail | null>(null);
  const [payeHelpOpen, setPayeHelpOpen] = useState(false);

  const { data: payslips = [], isLoading: payslipsLoading } = useQuery({
    queryKey: ['my-payslips'],
    queryFn: async () => {
      const res = await apiClient.get<{ items: PayslipDetail[] }>('/payroll/my-payslips?limit=24');
      return res.data.items;
    },
  });

  const ytdTotals = payslips.reduce(
    (acc, p) => ({
      gross: acc.gross + p.gross_pay,
      paye: acc.paye + p.paye,
      pension: acc.pension + p.pension,
      nhf: acc.nhf + p.nhf,
      nsitf: acc.nsitf + p.nsitf,
      itf: acc.itf + p.itf,
      total_deductions: acc.total_deductions + p.total_deductions,
      net: acc.net + p.net_pay,
    }),
    { gross: 0, paye: 0, pension: 0, nhf: 0, nsitf: 0, itf: 0, total_deductions: 0, net: 0 }
  );

  return (
    <Stack spacing={3}>
      {/* YTD Summary */}
      {payslips.length > 0 && (
        <Card sx={{ background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
          <CardContent>
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 2 }}>Year-to-Date Summary</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box>
                  <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', textTransform: 'uppercase', fontWeight: TYPE.semibold, mb: 0.5 }}>YTD Gross Pay</Typography>
                  <Typography sx={{ fontSize: TYPE.xl, fontWeight: TYPE.bold }}>{formatCurrency(ytdTotals.gross, currencyCode)}</Typography>
                  <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', mt: 0.5 }}>{payslips.length} payslip{payslips.length !== 1 ? 's' : ''}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box>
                  <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', textTransform: 'uppercase', fontWeight: TYPE.semibold, mb: 0.5 }}>YTD Deductions</Typography>
                  <Typography sx={{ fontSize: TYPE.xl, fontWeight: TYPE.bold, color: 'error.main' }}>{formatCurrency(ytdTotals.total_deductions, currencyCode)}</Typography>
                  <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', mt: 0.5 }}>{ytdTotals.gross > 0 ? ((ytdTotals.total_deductions / ytdTotals.gross) * 100).toFixed(1) : 0}% of gross</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box>
                  <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', textTransform: 'uppercase', fontWeight: TYPE.semibold, mb: 0.5 }}>YTD Net Pay</Typography>
                  <Typography sx={{ fontSize: TYPE.xl, fontWeight: TYPE.bold, color: 'success.main' }}>{formatCurrency(ytdTotals.net, currencyCode)}</Typography>
                  <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', mt: 0.5 }}>{ytdTotals.gross > 0 ? ((ytdTotals.net / ytdTotals.gross) * 100).toFixed(1) : 0}% take-home</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box>
                  <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', textTransform: 'uppercase', fontWeight: TYPE.semibold, mb: 0.5 }}>Average Monthly</Typography>
                  <Typography sx={{ fontSize: TYPE.xl, fontWeight: TYPE.bold }}>{formatCurrency(ytdTotals.net / Math.max(payslips.length, 1), currencyCode)}</Typography>
                  <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', mt: 0.5 }}>net per month</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* YTD Deductions Breakdown */}
      {payslips.length > 0 && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>Year-to-Date Deductions Breakdown</Typography>
              <Tooltip title="How are these calculated?">
                <IconButton size="small" onClick={() => setPayeHelpOpen(true)}>
                  <HelpIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontSize: TYPE.xs }}>Deduction Type</TableCell>
                  <TableCell align="right" sx={{ fontSize: TYPE.xs }}>YTD Amount</TableCell>
                  <TableCell align="right" sx={{ fontSize: TYPE.xs }}>% of Gross</TableCell>
                  <TableCell sx={{ fontSize: TYPE.xs }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ytdTotals.paye > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium }}>PAYE (Tax)</TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold }}>{formatCurrency(ytdTotals.paye, currencyCode)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.sm }}>{((ytdTotals.paye / ytdTotals.gross) * 100).toFixed(1)}%</TableCell>
                    <TableCell sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>Personal income tax (Finance Act 2020)</TableCell>
                  </TableRow>
                )}
                {ytdTotals.pension > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium }}>Pension Contribution</TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold }}>{formatCurrency(ytdTotals.pension, currencyCode)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.sm }}>{((ytdTotals.pension / ytdTotals.gross) * 100).toFixed(1)}%</TableCell>
                    <TableCell sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>Retirement savings (Pension Reform Act 2014)</TableCell>
                  </TableRow>
                )}
                {ytdTotals.nhf > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium }}>NHF (Housing Fund)</TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold }}>{formatCurrency(ytdTotals.nhf, currencyCode)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.sm }}>{((ytdTotals.nhf / ytdTotals.gross) * 100).toFixed(1)}%</TableCell>
                    <TableCell sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>National Housing Fund contribution</TableCell>
                  </TableRow>
                )}
                {ytdTotals.nsitf > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium }}>NSITF (Workman's Comp)</TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold }}>{formatCurrency(ytdTotals.nsitf, currencyCode)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.sm }}>{((ytdTotals.nsitf / ytdTotals.gross) * 100).toFixed(1)}%</TableCell>
                    <TableCell sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>Employee accident insurance</TableCell>
                  </TableRow>
                )}
                {ytdTotals.itf > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium }}>ITF (Training Fund)</TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold }}>{formatCurrency(ytdTotals.itf, currencyCode)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.sm }}>{((ytdTotals.itf / ytdTotals.gross) * 100).toFixed(1)}%</TableCell>
                    <TableCell sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>Skills development fund</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Payslip History */}
      <Card>
        <CardContent>
          <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 2 }}>Payslip History (Last 24 Months)</Typography>
          {payslipsLoading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : payslips.length === 0 ? (
            <Typography sx={{ color: 'text.secondary', fontSize: TYPE.sm, py: 3, textAlign: 'center' }}>
              No payslips available yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontSize: TYPE.xs }}>Period</TableCell>
                  <TableCell align="right" sx={{ fontSize: TYPE.xs }}>Gross Pay</TableCell>
                  <TableCell align="right" sx={{ fontSize: TYPE.xs }}>Total Deductions</TableCell>
                  <TableCell align="right" sx={{ fontSize: TYPE.xs }}>Net Pay</TableCell>
                  <TableCell sx={{ fontSize: TYPE.xs }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payslips.map((p: PayslipDetail) => {
                  const monthName = new Date(p.period_year, p.period_month - 1).toLocaleString('default', { month: 'short' });
                  return (
                    <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedPayslip(p)}>
                      <TableCell sx={{ fontSize: TYPE.sm }}>{monthName} {p.period_year}</TableCell>
                      <TableCell align="right" sx={{ fontSize: TYPE.sm }}>{formatCurrency(p.gross_pay, currencyCode)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>{formatCurrency(p.total_deductions, currencyCode)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: TYPE.semibold, fontSize: TYPE.sm }}>{formatCurrency(p.net_pay, currencyCode)}</TableCell>
                      <TableCell sx={{ fontSize: TYPE.xs }}>
                        <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setSelectedPayslip(p); }}
                          sx={{ textTransform: 'none', borderRadius: '6px' }}>
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Payslip Detail Modal */}
      <Dialog open={!!selectedPayslip} onClose={() => setSelectedPayslip(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <BrandedDialogTitle>
          {selectedPayslip && (() => {
            const monthName = new Date(selectedPayslip.period_year, selectedPayslip.period_month - 1).toLocaleString('default', { month: 'long' });
            return `${monthName} ${selectedPayslip.period_year} Payslip`;
          })()}
        </BrandedDialogTitle>
        <DialogContent>
          {selectedPayslip && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
                <InfoRow label="Gross Pay" value={formatCurrency(selectedPayslip.gross_pay, currencyCode)} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                  Deductions
                </Typography>
                {selectedPayslip.paye > 0 && <InfoRow label="PAYE" value={formatCurrency(selectedPayslip.paye, currencyCode)} />}
                {selectedPayslip.pension > 0 && <InfoRow label="Pension (Contribution)" value={formatCurrency(selectedPayslip.pension, currencyCode)} />}
                {selectedPayslip.nhf > 0 && <InfoRow label="NHF (Housing Fund)" value={formatCurrency(selectedPayslip.nhf, currencyCode)} />}
                {selectedPayslip.nsitf > 0 && <InfoRow label="NSITF (Workman's Comp)" value={formatCurrency(selectedPayslip.nsitf, currencyCode)} />}
                {selectedPayslip.itf > 0 && <InfoRow label="ITF (Training Fund)" value={formatCurrency(selectedPayslip.itf, currencyCode)} />}
              </Box>
              <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: '8px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                  <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>Total Deductions</Typography>
                  <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>{formatCurrency(selectedPayslip.total_deductions, currencyCode)}</Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: '8px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                  <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>Net Pay</Typography>
                  <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, color: 'primary.main' }}>{formatCurrency(selectedPayslip.net_pay, currencyCode)}</Typography>
                </Box>
              </Box>
              <PayslipExplainPanel lineId={selectedPayslip.id} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelectedPayslip(null)} sx={{ borderRadius: '8px', textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* PAYE Explanation Modal */}
      <PAYEExplanationModal open={payeHelpOpen} onClose={() => setPayeHelpOpen(false)} />
    </Stack>
  );
}
